import { z } from 'zod';
import { fleetRecoverySha256Schema as hash, fleetRecoveryUuidSchema as uuid, fleetRecoveryIdSchema as id,
  fleetRecoveryUtcSchema as utc } from '../fleetRecoverySchemas';
export const qualificationAttestationV1Schema = z.object({ schemaVersion: z.literal(1), qualificationId: uuid,
  artifactSetSha256: hash, sourceCommit: z.string().regex(/^[a-f0-9]{40}$/), authorityConfigurationSha256: hash,
  infrastructureDeploymentId: id, coveragePolicySha256: hash, cohortInventorySha256: hash, requiredCaseInventorySha256: hash,
  evidenceRootSha256: hash, performanceEvidenceSha256: hash, independentObserverKeyIds: z.array(id).min(1).max(128),
  issuedAtUtc: utc, expiresAtUtc: utc,
  result: z.object({ required: z.number().int().min(137).max(1000000), passed: z.number().int().min(0).max(1000000),
    failed: z.literal(0), unavailable: z.literal(0), skipped: z.literal(0) }).strict(),
}).strict().superRefine((a, ctx) => {
  if (a.result.required !== a.result.passed || new Set(a.independentObserverKeyIds).size !== a.independentObserverKeyIds.length
    || Date.parse(a.expiresAtUtc) <= Date.parse(a.issuedAtUtc)
    || Date.parse(a.expiresAtUtc) - Date.parse(a.issuedAtUtc) > 86400000)
    ctx.addIssue({ code: 'custom', message: 'Incomplete, ambiguous or overlong qualification' });
});
export type QualificationAttestationV1 = z.infer<typeof qualificationAttestationV1Schema>;

/** Compare trusted verifier-derived bindings; caller-supplied counts cannot reduce coverage. */
export class RecoveryQualificationPolicyV1 {
  public static assertCoverage(actual: readonly string[], required: readonly string[]): void {
    if (required.length < 137 || required.length > 1000000 || new Set(required).size !== required.length
      || new Set(actual).size !== actual.length || actual.length !== required.length
      || actual.some(id => !required.includes(id))) throw new Error('qualification_case_inventory_mismatch');
  }
  public static assertMatches(attestation: QualificationAttestationV1, expected: QualificationAttestationV1, now: number): void {
    const parsed = qualificationAttestationV1Schema.parse(attestation);
    if (!Number.isFinite(now) || now < Date.parse(parsed.issuedAtUtc) || now >= Date.parse(parsed.expiresAtUtc)) throw new Error('qualification_expired');
    for (const key of ['artifactSetSha256', 'sourceCommit', 'authorityConfigurationSha256', 'infrastructureDeploymentId',
      'coveragePolicySha256', 'cohortInventorySha256', 'requiredCaseInventorySha256', 'evidenceRootSha256', 'performanceEvidenceSha256'] as const)
      if (parsed[key] !== expected[key]) throw new Error('qualification_binding_mismatch');
    if (parsed.result.required !== expected.result.required || parsed.independentObserverKeyIds.slice().sort().join('\n')
      !== expected.independentObserverKeyIds.slice().sort().join('\n')) throw new Error('qualification_coverage_mismatch');
  }
}
